import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, ArrowRight, BookOpen, CheckCircle2, Building2, Sparkles, Handshake, TrendingUp, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import LegalFooter from "@/components/LegalFooter";

/**
 * /franchise — Public application form for operators who want to license Link Ride
 * in their own city. Submits to POST /api/franchise/apply which stores the
 * record in db.franchise_applications, records legal acceptances in
 * db.legal_acceptances, and emails the owner.
 */
export default function FranchiseApply() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [legalDocs, setLegalDocs] = useState([]);
  const [acceptedDocIds, setAcceptedDocIds] = useState(new Set());
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    city: "",
    state: "",
    estimated_fleet_size: "",
    website: "",
    note: "",
    referral_source: "",
    // Compliance qualifying fields
    legal_business_name: "",
    ein: "",
    states_of_operation: "",
    commercial_insurance_status: "",
    tnc_permit_status: "",
    responsible_party_name: "",
    responsible_party_title: "",
    operates_own_fleet: false,
    licenses_only: false,
  });

  // Fetch the current legal-docs library. We only require acceptance of
  // BUSINESS-category docs at signup (SaaS License, White-Label, DPA, SLA).
  // The consumer docs are provided for reference.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/legal/documents`)
      .then((r) => {
        if (cancelled) return;
        setLegalDocs(r.data?.documents || []);
      })
      .catch(() => {
        if (!cancelled) setLegalDocs([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const requiredDocs = legalDocs.filter(
    (d) => d.category === "Business" || d.id === "aup"
  );

  const toggleAcceptance = (docId) => {
    setAcceptedDocIds((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const update = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));
  const updateBool = (k) => (checked) => setForm((prev) => ({ ...prev, [k]: !!checked }));

  const submit = async (e) => {
    e.preventDefault();
    // Minimal validation
    if (form.business_name.trim().length < 2) return toast.error("Business name required");
    if (form.contact_name.trim().length < 2) return toast.error("Your name required");
    if (!/@/.test(form.email)) return toast.error("Valid email required");
    if (form.phone.trim().length < 7) return toast.error("Valid phone required");
    if (form.city.trim().length < 2) return toast.error("City required");
    if (form.state.trim().length < 2) return toast.error("State required");
    if (form.responsible_party_name.trim().length < 2) return toast.error("Legally responsible party name required");
    if (!form.commercial_insurance_status) return toast.error("Select an insurance status");
    if (!form.tnc_permit_status) return toast.error("Select a TNC permit status");
    if (!form.operates_own_fleet && !form.licenses_only) {
      return toast.error("Select operating model (own fleet, licenses-only, or both)");
    }

    const missing = requiredDocs.filter((d) => !acceptedDocIds.has(d.id));
    if (missing.length > 0) {
      return toast.error(`Please review and accept: ${missing.map((d) => d.title).join(", ")}`);
    }

    setSubmitting(true);
    try {
      const states = form.states_of_operation
        .split(",")
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean)
        .slice(0, 20);
      const payload = {
        business_name: form.business_name.trim(),
        contact_name: form.contact_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        estimated_fleet_size: form.estimated_fleet_size
          ? Number.parseInt(form.estimated_fleet_size, 10) || null
          : null,
        website: form.website.trim() || null,
        note: form.note.trim() || null,
        referral_source: form.referral_source.trim() || null,
        qualifying: {
          legal_business_name: form.legal_business_name.trim() || null,
          ein: form.ein.trim() || null,
          states_of_operation: states.length ? states : null,
          commercial_insurance_status: form.commercial_insurance_status || null,
          tnc_permit_status: form.tnc_permit_status || null,
          responsible_party_name: form.responsible_party_name.trim() || null,
          responsible_party_title: form.responsible_party_title.trim() || null,
          operates_own_fleet: form.operates_own_fleet,
          licenses_only: form.licenses_only,
        },
        acceptances: Array.from(acceptedDocIds).map((docId) => {
          const d = legalDocs.find((x) => x.id === docId);
          return {
            doc_id: docId,
            doc_version: d?.version || "1.0",
            accepted: true,
          };
        }),
      };
      const res = await axios.post(`${API}/franchise/apply`, payload);
      if (res.data?.ok) {
        setSubmitted(true);
        toast.success("Application received — we'll be in touch soon.");
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Something went wrong";
      toast.error(typeof msg === "string" ? msg : "Please double-check the form and try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <Card className="bg-[#121212] border-white/5 max-w-md w-full" data-testid="franchise-thanks-card">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold">Application received</h1>
            <p className="text-sm text-zinc-400">
              Thanks {form.contact_name || "for reaching out"} — we&apos;ll review your application and reach out at <span className="text-white">{form.email}</span> within 2-3 business days.
            </p>
            <Button
              onClick={() => navigate("/")}
              className="w-full bg-[#0047FF] hover:bg-[#0047FF]/90"
              data-testid="franchise-back-home-btn"
            >
              Back to Link Ride
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 z-10 backdrop-blur-md bg-black/70 border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="text-zinc-400 hover:text-white -ml-2"
            data-testid="franchise-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="ml-auto text-xs text-zinc-500">Franchise inquiry</div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Hero */}
        <div className="space-y-4" data-testid="franchise-hero">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#0047FF]/10 border border-[#0047FF]/30 px-3 py-1 text-xs text-[#7AADFF]">
            <Sparkles className="w-3.5 h-3.5" /> Now licensing to operators
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight">
            Run <span className="text-[#0047FF]">Link Ride</span> in your city.
          </h1>
          <p className="text-base text-zinc-400 max-w-2xl">
            License the same rides + delivery platform we run in Reading, PA. You keep your local branding,
            your drivers, and the majority of every fare — we handle the tech, dispatch, payments, and Stripe payouts.
          </p>
          <div className="pt-1 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate('/help?role=franchise')}
              className="inline-flex items-center gap-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#0047FF]/40 px-4 py-2 text-sm text-white transition-colors"
              data-testid="franchise-learn-more-pill"
            >
              <BookOpen className="w-4 h-4 text-[#4D88FF]" />
              <span>Learn more before you apply</span>
              <ArrowRight className="w-3.5 h-3.5 text-white/60" />
            </button>
            <span
              className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 text-xs font-semibold text-emerald-200"
              data-testid="franchise-trial-pill"
            >
              🎁 30-day operator trial — no card required
            </span>
          </div>
        </div>

        {/* Trial callout — sets the expectation before applicants
            fill the form. Matches the /help "For Franchisees" section. */}
        <div
          className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-emerald-500/[0.02] p-5"
          data-testid="franchise-trial-callout"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/25 flex items-center justify-center flex-shrink-0">
              <Handshake className="w-5 h-5 text-emerald-200" />
            </div>
            <div className="space-y-1.5">
              <p className="text-sm font-bold text-emerald-100 uppercase tracking-wider">Your first 30 days are free</p>
              <p className="text-sm text-emerald-50/90 leading-snug">
                Zero platform fee. You keep 100% of every fare (minus Stripe card fees). Convert to a paid tenant
                whenever you're ready — or suspend anytime with no commitment. We provision your tenant
                within 1-2 business days of approval, then hop on a 30-minute onboarding call to get you live.
              </p>
              <p className="text-xs text-emerald-100/70">
                Full onboarding timeline + prep checklist:{" "}
                <button type="button" onClick={() => navigate('/help?role=franchise')} className="underline hover:text-emerald-100">
                  see the franchise guide →
                </button>
              </p>
            </div>
          </div>
        </div>

        {/* Value props */}
        <div className="grid sm:grid-cols-3 gap-3">
          <Card className="bg-[#121212] border-white/5">
            <CardContent className="p-4 space-y-2">
              <Building2 className="w-5 h-5 text-[#4D88FF]" />
              <p className="text-sm text-white font-semibold">Your city, your brand</p>
              <p className="text-xs text-zinc-500">Subdomain + logo + colors. Customers see you, not us.</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-white/5">
            <CardContent className="p-4 space-y-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              <p className="text-sm text-white font-semibold">Direct payouts</p>
              <p className="text-xs text-zinc-500">Your Stripe Connect account. Money hits your bank, not ours.</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-white/5">
            <CardContent className="p-4 space-y-2">
              <Handshake className="w-5 h-5 text-amber-400" />
              <p className="text-sm text-white font-semibold">Simple platform fee</p>
              <p className="text-xs text-zinc-500">Small % of gross bookings after your 30-day trial. No monthly minimums.</p>
            </CardContent>
          </Card>
        </div>

        {/* Application form */}
        <Card className="bg-[#121212] border-white/5" data-testid="franchise-form-card">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold mb-1">Tell us about your operation</h2>
            <p className="text-xs text-zinc-500 mb-6">
              We review applications weekly. Once approved, we schedule a 30-minute onboarding call to configure your tenant.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="business_name">Business name *</Label>
                  <Input
                    id="business_name"
                    value={form.business_name}
                    onChange={update("business_name")}
                    className="bg-black border-white/10"
                    placeholder="Acme Rides LLC"
                    data-testid="franchise-input-business-name"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="contact_name">Your name *</Label>
                  <Input
                    id="contact_name"
                    value={form.contact_name}
                    onChange={update("contact_name")}
                    className="bg-black border-white/10"
                    placeholder="Jane Doe"
                    data-testid="franchise-input-contact-name"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="bg-black border-white/10"
                    placeholder="you@yourcity.com"
                    data-testid="franchise-input-email"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className="bg-black border-white/10"
                    placeholder="(555) 123-4567"
                    data-testid="franchise-input-phone"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={form.city}
                    onChange={update("city")}
                    className="bg-black border-white/10"
                    placeholder="Philadelphia"
                    data-testid="franchise-input-city"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    value={form.state}
                    onChange={update("state")}
                    className="bg-black border-white/10"
                    placeholder="PA"
                    data-testid="franchise-input-state"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="fleet">Estimated fleet size</Label>
                  <Input
                    id="fleet"
                    type="number"
                    min="0"
                    value={form.estimated_fleet_size}
                    onChange={update("estimated_fleet_size")}
                    className="bg-black border-white/10"
                    placeholder="10"
                    data-testid="franchise-input-fleet"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="website">Existing website (optional)</Label>
                  <Input
                    id="website"
                    value={form.website}
                    onChange={update("website")}
                    className="bg-black border-white/10"
                    placeholder="https://..."
                    data-testid="franchise-input-website"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="referral">How did you hear about us?</Label>
                <Input
                  id="referral"
                  value={form.referral_source}
                  onChange={update("referral_source")}
                  className="bg-black border-white/10"
                  placeholder="Instagram, a friend, Google..."
                  data-testid="franchise-input-referral"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="note">Tell us more (optional)</Label>
                <Textarea
                  id="note"
                  value={form.note}
                  onChange={update("note")}
                  rows={4}
                  className="bg-black border-white/10"
                  placeholder="Anything else we should know about your city, drivers, or timeline."
                  data-testid="franchise-input-note"
                />
              </div>

              {/* Compliance / qualifying section — collects the info an
                  operator needs on record before we license the software.
                  These are attestations; verification happens post-approval. */}
              <div className="pt-6 border-t border-white/5 space-y-4" data-testid="franchise-qualifying-section">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-lg font-bold">Compliance & operating model</h3>
                </div>
                <p className="text-xs text-zinc-500">
                  You are the operator of record — you are responsible for local TNC, insurance, and licensing compliance in every state you operate. These answers put that on the record.
                </p>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="legal_business_name">Legal business name</Label>
                    <Input
                      id="legal_business_name"
                      value={form.legal_business_name}
                      onChange={update("legal_business_name")}
                      className="bg-black border-white/10"
                      placeholder="Smith Transportation LLC"
                      data-testid="franchise-input-legal-name"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="ein">EIN / Tax ID</Label>
                    <Input
                      id="ein"
                      value={form.ein}
                      onChange={update("ein")}
                      className="bg-black border-white/10"
                      placeholder="12-3456789"
                      data-testid="franchise-input-ein"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="states_of_operation">States of operation (comma-separated)</Label>
                  <Input
                    id="states_of_operation"
                    value={form.states_of_operation}
                    onChange={update("states_of_operation")}
                    className="bg-black border-white/10"
                    placeholder="PA, NJ, DE"
                    data-testid="franchise-input-states-op"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Commercial insurance</Label>
                    <select
                      value={form.commercial_insurance_status}
                      onChange={update("commercial_insurance_status")}
                      className="w-full h-9 rounded-md bg-black border border-white/10 px-3 text-sm"
                      data-testid="franchise-select-insurance"
                    >
                      <option value="">Select…</option>
                      <option value="active">Active policy in place</option>
                      <option value="pending">Application in progress</option>
                      <option value="none">No commercial policy yet</option>
                      <option value="not_applicable">Not applicable (licensing only)</option>
                    </select>
                    {(form.commercial_insurance_status === "none" || form.commercial_insurance_status === "pending") && (
                      <a
                        href="https://www.progressivecommercial.com/commercial-auto/rideshare-insurance/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-[#4D88FF] hover:text-[#7AADFF] hover:underline mt-1"
                        data-testid="insurance-broker-link"
                      >
                        Get commercial coverage →
                      </a>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label>TNC / rideshare permit</Label>
                    <select
                      value={form.tnc_permit_status}
                      onChange={update("tnc_permit_status")}
                      className="w-full h-9 rounded-md bg-black border border-white/10 px-3 text-sm"
                      data-testid="franchise-select-tnc"
                    >
                      <option value="">Select…</option>
                      <option value="active">Approved / active</option>
                      <option value="pending">Application filed</option>
                      <option value="not_required">Not required in my jurisdiction</option>
                      <option value="unsure">Unsure — need guidance</option>
                    </select>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="responsible_party_name">Responsible party (name) *</Label>
                    <Input
                      id="responsible_party_name"
                      value={form.responsible_party_name}
                      onChange={update("responsible_party_name")}
                      className="bg-black border-white/10"
                      placeholder="Jane Smith"
                      data-testid="franchise-input-responsible-name"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="responsible_party_title">Title / role</Label>
                    <Input
                      id="responsible_party_title"
                      value={form.responsible_party_title}
                      onChange={update("responsible_party_title")}
                      className="bg-black border-white/10"
                      placeholder="Owner / Founder"
                      data-testid="franchise-input-responsible-title"
                    />
                  </div>
                </div>

                <div className="space-y-2 pt-2">
                  <Label className="text-xs text-zinc-400">Operating model (select all that apply)</Label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <Checkbox
                      checked={form.operates_own_fleet}
                      onCheckedChange={updateBool("operates_own_fleet")}
                      data-testid="franchise-check-own-fleet"
                    />
                    <span className="text-sm text-zinc-300 leading-tight">
                      I operate my own fleet of drivers/vehicles under my brand
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <Checkbox
                      checked={form.licenses_only}
                      onCheckedChange={updateBool("licenses_only")}
                      data-testid="franchise-check-licenses-only"
                    />
                    <span className="text-sm text-zinc-300 leading-tight">
                      I only sub-license the software to other operators (I don't run rides directly)
                    </span>
                  </label>
                </div>
              </div>

              {/* Legal document acceptance — required before submission */}
              <div className="pt-6 border-t border-white/5 space-y-4" data-testid="franchise-acceptance-section">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  <h3 className="text-lg font-bold">Agreements</h3>
                </div>
                <div className="p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-200/90 leading-relaxed">
                  These documents are DRAFTS pending attorney review. Please read each one — you will be bound by the executed versions once we counter-sign.
                </div>
                {requiredDocs.length === 0 && (
                  <p className="text-xs text-zinc-500">Loading agreements…</p>
                )}
                {requiredDocs.map((d) => (
                  <label
                    key={d.id}
                    className="flex items-start gap-3 p-3 rounded-md border border-white/5 bg-black/40 hover:border-[#0047FF]/30 cursor-pointer transition-colors"
                    data-testid={`franchise-acceptance-${d.id}`}
                  >
                    <Checkbox
                      checked={acceptedDocIds.has(d.id)}
                      onCheckedChange={() => toggleAcceptance(d.id)}
                      className="mt-1"
                      data-testid={`franchise-accept-checkbox-${d.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white">
                        I have read and agree to the <Link to={`/legal/${d.id}`} target="_blank" className="text-[#4D88FF] hover:underline" data-testid={`franchise-doclink-${d.id}`}>{d.title}</Link>
                      </p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">v{d.version} · {d.effective_date} — {d.summary}</p>
                    </div>
                  </label>
                ))}
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#0047FF] hover:bg-[#0047FF]/90 h-11 text-base"
                data-testid="franchise-submit-btn"
              >
                {submitting ? "Submitting..." : "Submit application"}
              </Button>
              <p className="text-[11px] text-zinc-500 text-center">
                We&apos;ll never share your info. Not a commitment.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
      <LegalFooter />
    </div>
  );
}
